import type { Timestamp } from "firebase/firestore";
import type { CasinoCategory } from "@/features/casino/models/casinoCategory";
import type { CasinoGame } from "@/features/casino/models/casinoGame";
import type { CasinoBet } from "@/features/casino/models/casinoBet";
import type { CasinoProvider } from "@/features/casino/models/casinoProvider";
import type { CategoryCountsByProduct } from "@/features/casino/models/casinoCategoryCounts";
import type { Direction } from "@/features/casino/models/casinoCategoryGridFilters";
import { INT32_MAX } from "@/features/casino/utils/constants";
import { truncateDecimals } from "@/utilities/number";

/**
 * Real or Fun Play
 * @param game
 */
const playModeMap: Record<"Real" | "Fun", number> = {
  Real: 0,
  Fun: 1,
};

const getCategoryPath = (categoryId: string) =>
  `/casino/categories/${categoryId}/`;

const getCategoryTabPath = (categoryId: string) => `/casino/${categoryId}/`;

const isRecentlyPlayedCategory = (category?: CasinoCategory) =>
  category?.slug === "recently-played";

const isNewGamesCategory = (category?: CasinoCategory) =>
  category?.slug === "new-games";

/**
 * Game ids most recently played first.
 */
const sortByLastPlayed = (recentlyPlayed?: Record<string, Timestamp>) => {
  return Object.entries(recentlyPlayed ?? {})
    .sort(([, a], [, b]) => b.toMillis() - a.toMillis())
    .map(([gameId]) => gameId);
};

const getCategoriesWithGames = (
  countsByProduct: CategoryCountsByProduct,
  categories: CasinoCategory[],
  hasRecentlyPlayed = false,
): CasinoCategory[] => {
  const withGames = new Set<string>();

  Object.values(countsByProduct).forEach((entries) => {
    entries.forEach(({ categoryId, count }) => {
      if (count > 0) withGames.add(categoryId);
    });
  });

  // Recently Played is per user and doesn't come from countsByProduct
  return categories.filter((category) =>
    isRecentlyPlayedCategory(category)
      ? hasRecentlyPlayed
      : withGames.has(category.id),
  );
};

/**
 * Resolve category ids against the published categories.
 */
const getGameCategories = (
  categoryIds: string[],
  categories: CasinoCategory[],
) => {
  const ids = new Set(categoryIds);

  return categories.filter((category) => ids.has(category.id));
};

const getGamePath = (gameSlug: string) => `/casino/games/${gameSlug}/`;

const getProviderPath = (slug: string) => `/casino/providers/${slug}/`;

const processCasinoGame = (
  game: CasinoGame & { id: string },
  categories: CasinoCategory[] = [],
) => {
  // picking the first category grabs the highest priority category
  const firstCategory = getGameCategories(game.categories, categories)[0];

  return {
    ...game,
    firstCategory,
    categoryLabel: firstCategory?.name,
    gamePath: getGamePath(game.slug),
    categoryPath: firstCategory && getCategoryPath(firstCategory.id),
    providerPath: getProviderPath(game.productSlug),
  };
};

const processCasinoBet = (
  bet: CasinoBet,
  categories: CasinoCategory[] = [],
) => {
  const firstCategory = getGameCategories(bet.categoryIds, categories)[0];

  return {
    ...bet,
    firstCategory,
    categoryLabel: firstCategory?.name,
    gamePath: getGamePath(bet.gameSlug),
    categoryPath: firstCategory && getCategoryPath(firstCategory.id),
    providerPath: getProviderPath(bet.productSlug),
  };
};

const processCasinoProvider = (provider: CasinoProvider & { id: string }) => {
  return {
    ...provider,
    providerPath: getProviderPath(provider.slug),
  };
};

type CategoryOrderedGame = Pick<CasinoGame, "name" | "categoryPriorities">;

const getCategoryPriority = (game: CategoryOrderedGame, categoryId: string) =>
  game.categoryPriorities?.[categoryId] ?? INT32_MAX;

/**
 * Admin has ranked the game in this category. A missing key or the default
 * INT32_MAX means unranked.
 */
const isRankedInCategory = (game: CategoryOrderedGame, categoryId: string) =>
  getCategoryPriority(game, categoryId) < INT32_MAX;

/**
 * A category's default order from its two queries: ranked games by their
 * priority in the category (name breaks ties), then the rest by name.
 * @param ranked games with a priority below INT32_MAX
 * @param rest the category's games by name
 * @returns the first `limit` games, and how many were merged for hasMore
 */
const mergeCategoryOrderedGames = <T extends CategoryOrderedGame>({
  ranked,
  rest,
  categoryId,
  direction,
  limit,
}: {
  ranked: T[];
  rest: T[];
  categoryId: string;
  direction: Direction;
  limit: number;
}) => {
  const dir = direction === "asc" ? 1 : -1;

  // copy first, the query data is shared with the cache
  const sortedRanked = [...ranked].sort(
    (a, b) =>
      dir *
      (getCategoryPriority(a, categoryId) -
        getCategoryPriority(b, categoryId) || a.name.localeCompare(b.name)),
  );

  // rest holds ranked games too, which are already placed above
  const unranked = rest.filter((game) => !isRankedInCategory(game, categoryId));

  const merged =
    direction === "asc"
      ? [...sortedRanked, ...unranked]
      : [...unranked, ...sortedRanked];

  return { games: merged.slice(0, limit), total: merged.length };
};

const formatRTP = (rtp: string | number) => {
  const num = Number(rtp);
  if (isNaN(num)) return null;

  const truncated = truncateDecimals(num, 2);
  return `${truncated}%`;
};

export {
  playModeMap,
  isRecentlyPlayedCategory,
  isNewGamesCategory,
  sortByLastPlayed,
  getCategoriesWithGames,
  getGameCategories,
  processCasinoGame,
  processCasinoBet,
  processCasinoProvider,
  getGamePath,
  getCategoryPath,
  getCategoryTabPath,
  getProviderPath,
  formatRTP,
  isRankedInCategory,
  mergeCategoryOrderedGames,
};
